import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ShopifyService } from '../../services/shopify.service';
import { CartService } from '../../services/cart.service';
import { DesignQueueService } from '../../services/design-queue.service';
import { ShopifyProduct } from '../models';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-all-magnets',
    templateUrl: './all-magnets.component.html',
    styleUrls: ['./all-magnets.component.scss'],
    imports: [FormsModule, TranslateModule],
})
export class AllMagnetsComponent implements OnInit, OnDestroy {
  allProducts: ShopifyProduct[] = [];
  filteredProducts: ShopifyProduct[] = [];
  searchQuery: string = '';
  isLoading: boolean = true;
  loadError: boolean = false;
  showAddedToCartNotification: boolean = false;
  addedProductName: string = '';
  showCartModal: boolean = false;

  comingFromFinishOrder: boolean = false;

  private subscriptions: Subscription[] = [];
  private notificationTimeout: any;

  get isMainStore(): boolean {
    return sessionStorage.getItem('main') === 'true';
  }

  get totalCartItems(): number {
    return this.cartService.getTotalQuantity();
  }

  get designQueue() {
    return this.designQueueService.getDesigns();
  }

  get price(): { price?: { amount?: string; currencyCode?: string } } | null {
    const sessionPrice = sessionStorage.getItem('price');
    return sessionPrice ? JSON.parse(sessionPrice) : null;
  }

  get customMagnetUnitPrice(): number {
    const p = this.price?.price?.amount;
    return p != null ? parseFloat(String(p)) : 0;
  }

  get cartModalCurrency(): string {
    return this.price?.price?.currencyCode || 'EUR';
  }

  get cartModalTotal(): number {
    return this.cartService.getTotalPrice(
      this.price?.price?.amount ? parseFloat(this.price.price.amount) : 0
    );
  }

  get cartShopifyProducts(): Array<{ product: ShopifyProduct; quantity: number }> {
    return this.cartService.getShopifyProducts();
  }

  openCart(): void {
    this.showCartModal = true;
  }

  closeCartModal(): void {
    this.showCartModal = false;
  }

  formatCartPrice(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.cartModalCurrency
    }).format(amount);
  }

  onUpdateDesignQuantity(event: { id: string; quantity: number }): void {
    this.designQueueService.updateDesignQuantity(event.id, event.quantity);
  }

  updateCartShopifyQuantity(productId: string, quantity: number): void {
    this.cartService.updateShopifyProductQuantity(productId, quantity);
  }

  removeCartShopifyProduct(productId: string): void {
    this.cartService.removeShopifyProduct(productId);
    if (this.cartService.getTotalQuantity() === 0) {
      this.closeCartModal();
    }
  }

  checkoutFromModal(): void {
    this.closeCartModal();
    this.router.navigate(['/home'], { queryParams: { openCart: '1', checkout: '1' } });
  }

  constructor(
    private router: Router,
    private shopifyService: ShopifyService,
    private cartService: CartService,
    private designQueueService: DesignQueueService
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.comingFromFinishOrder = !!(history.state && (history.state as any).fromFinishOrder);
    this.loadProducts();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  }

  goBack(): void {
    if (this.comingFromFinishOrder) {
      this.router.navigate(['/home'], { queryParams: { finishOrderView: '1' } });
    } else {
      this.router.navigate(['/home']);
    }
  }

  private loadProducts(after?: string, accumulated: ShopifyProduct[] = []): void {
    this.isLoading = true;
    this.loadError = false;

    const sub = this.shopifyService.getProducts(250, undefined, after).subscribe({
      next: (response) => {
        if (response?.data?.products) {
          const products = this.parseProducts(response.data.products.edges);
          const allFetched = [...accumulated, ...products];

          const pageInfo = response.data.products.pageInfo;

          if (pageInfo.hasNextPage && pageInfo.endCursor) {
            this.loadProducts(pageInfo.endCursor, allFetched);
          } else {
            // Exclude Custom magnet, then sort showInitial first
            const filtered = allFetched.filter(p => !this.isCustomMagnetProduct(p));
            this.allProducts = this.sortByShowInitial(filtered);
            this.filteredProducts = [...this.allProducts];
            this.isLoading = false;
          }
        } else {
          this.loadError = true;
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading products:', error);
        this.loadError = true;
        this.isLoading = false;
      }
    });

    this.subscriptions.push(sub);
  }

  /** Exclude the Custom magnet product (used for make-yourself flow). */
  private isCustomMagnetProduct(product: ShopifyProduct): boolean {
    const title = (product.title || '').trim().toLowerCase();
    return title === 'custom magnet' || title === 'custom magnets';
  }

  private parseProducts(edges: any[]): ShopifyProduct[] {
    return edges
      .map(edge => edge.node)
      .filter(node => node.availableForSale && node.variants.edges.length > 0)
      .map(node => {
        const variant = node.variants.edges[0].node;
        const showInitial = node.metafield?.value === 'true';
        return {
          id: node.id,
          title: node.title,
          description: node.description || '',
          handle: node.handle,
          imageUrl: node.images.edges.length > 0 ? node.images.edges[0].node.url : '',
          price: {
            amount: variant.price.amount,
            currencyCode: variant.price.currencyCode
          },
          compareAtPrice: variant.compareAtPrice ? {
            amount: variant.compareAtPrice.amount,
            currencyCode: variant.compareAtPrice.currencyCode
          } : undefined,
          variantId: variant.id,
          availableForSale: variant.availableForSale,
          showInitial
        } as ShopifyProduct;
      });
  }

  private sortByShowInitial(products: ShopifyProduct[]): ShopifyProduct[] {
    return [...products].sort((a, b) => (b.showInitial ? 1 : 0) - (a.showInitial ? 1 : 0));
  }

  onSearchChange(): void {
    const query = this.searchQuery.toLowerCase().trim();

    if (query === '') {
      this.filteredProducts = [...this.allProducts];
    } else {
      this.filteredProducts = this.allProducts.filter(product =>
        product.title.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query)
      );
    }
  }

  addToCart(product: ShopifyProduct): void {
    this.cartService.addShopifyProduct(product, 1);

    this.addedProductName = product.title;
    this.showAddedToCartNotification = true;

    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    this.notificationTimeout = setTimeout(() => {
      this.showAddedToCartNotification = false;
    }, 3000);
  }

  formatPrice(amount: string, currencyCode: string): string {
    const price = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(price);
  }

  hasDiscount(product: ShopifyProduct): boolean {
    if (!product.compareAtPrice) return false;
    return parseFloat(product.compareAtPrice.amount) > parseFloat(product.price.amount);
  }

  getDiscountPercentage(product: ShopifyProduct): number {
    if (!product.compareAtPrice) return 0;
    const original = parseFloat(product.compareAtPrice.amount);
    const current = parseFloat(product.price.amount);
    return Math.round(((original - current) / original) * 100);
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    const loader = img.parentElement?.querySelector('.image-loader');
    if (loader) {
      loader.classList.add('loaded');
    }
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const loader = img.parentElement?.querySelector('.image-loader');
    if (loader) {
      loader.classList.add('loaded');
    }
  }
}
